'use client'

import { cn } from '@nwwp/ui-shadcn'
import { ScheduleCallCTA } from '../../../controls/ScheduleCallCTA'
import { buildScheduleRedirectUrl, PAID_MEDIA_CALENDLY_URL } from '../../../utils/ctas'
import { buildRows, narrative, strayLine, strays } from '../_lib/results'
import type { NarrativeBlock, SpendCheckState } from '../types'
import { ComparisonChart } from './ComparisonChart'

const SPEND_CHECK_SCHEDULE_URL = buildScheduleRedirectUrl(PAID_MEDIA_CALENDLY_URL, {})

const RULE: Record<NarrativeBlock['tone'], string> = {
  accent: 'border-green-darker',
  fall: 'border-neutral-darker',
  rise: 'border-gold',
}

function Legend() {
  const entries = [
    { tone: 'bg-neutral-darker', label: 'Falls, your spending was lower relative to the value’s priority' },
    { tone: 'bg-gold', label: 'Rises, your spending was higher relative to the value’s priority' },
    { tone: 'bg-smoke', label: 'Holds, your spending matched your value’s priority' },
  ]
  return (
    <ul className="mt-5 flex flex-col gap-1.5">
      {entries.map((e) => (
        <li key={e.label} className="flex items-center gap-2 text-body-xs text-neutral-darker">
          <span aria-hidden="true" className={cn('inline-block h-0.5 w-5 rounded-full', e.tone)} />
          {e.label}
        </li>
      ))}
    </ul>
  )
}

export function ResultsView({ state, onRestart }: { state: SpendCheckState; onRestart: () => void }) {
  const rows = buildRows(state)
  const blocks = narrative(rows)
  const [opening, ...rest] = blocks
  const stray = strays(state)

  return (
    <div>
      <h1 className="text-title-md text-gray-darker">
        What you <em className="text-green-darker">value</em>, what you <em className="text-green-darker">spend</em>
      </h1>
      <p className="mt-3 max-w-150 text-body text-neutral-darker">
        Your two lists side by side. The lines show where your values ranked compared to your spending.
      </p>

      {opening && (
        <section className={cn('mt-6 border-l-2 pl-4', RULE[opening.tone])}>
          <h2 className="text-body-sm-bold text-gray-darker">{opening.title}</h2>
          {opening.paragraphs.map((p) => (
            <p key={p} className="mt-2 text-body-sm text-neutral-darker">
              {p}
            </p>
          ))}
        </section>
      )}

      <Legend />

      <div className="mt-4 overflow-x-auto rounded-2xl border border-smoke bg-card p-4">
        <ComparisonChart rows={rows} />
      </div>

      {stray.length > 0 && (
        <section className="mt-5 border-l-2 border-smoke pl-4">
          <h3 className="text-body-sm-bold text-gray-darker">Spending outside your list</h3>
          {stray.map((s) => (
            <p key={s.k} className="mt-2 text-body-sm text-neutral-darker">
              {strayLine(s)}
            </p>
          ))}
        </section>
      )}

      {rest.map((block) => (
        <section key={block.title} className={cn('mt-5 border-l-2 pl-4', RULE[block.tone])}>
          <h3 className="text-body-sm-bold text-gray-darker">{block.title}</h3>
          {block.paragraphs.map((p) => (
            <p key={p} className="mt-2 text-body-sm text-neutral-darker">
              {p}
            </p>
          ))}
        </section>
      ))}

      {state.showAdvisorCta ? (
        <section className="mt-8 rounded-2xl border border-smoke bg-gradient-to-br from-bone-light to-bone-warm p-6 text-center">
          <h3 className="text-title-sm text-gray-darker">You know your values, now let&apos;s make a plan</h3>
          <p className="mx-auto mt-2 mb-4 max-w-130 text-body-sm text-neutral-darker">
            Share your values and financial goals with one of our financial advisors and see how a financial plan can
            map out what it would take to get there.
          </p>
          <ScheduleCallCTA
            baseUrl={SPEND_CHECK_SCHEDULE_URL}
            segmentElement="Spend Check Results CTA"
            size="cta-sm"
            variant="cta-dark"
          >
            Schedule a call
          </ScheduleCallCTA>
          <p className="mx-auto mt-4 max-w-130 border-t border-smoke pt-4 text-body-xs text-neutral-darker">
            NWWP is an SEC-registered investment adviser. Registration does not imply skill or training. Tool by
            NerdWallet, Inc., an affiliate, for informational purposes only.
          </p>
        </section>
      ) : null}

      <div className="mt-5">
        <button type="button" onClick={onRestart} className="text-body-sm-bold text-green-dark underline">
          Start over
        </button>
      </div>
    </div>
  )
}
